import { addDaysToDateString, getLocalDateString } from '@crm-src/lib/date-utils';

import type { CrmDocumentType } from './documentNumber';
import { createProductLine } from './productLines';

/**
 * Payment terms: every invoice/quote is due 30 days from its issue date. This
 * is the SINGLE source of the "+30" rule — the initial form state, the
 * document-load defaults (`data-loaders.ts`), and the issue-date → due-date
 * sync all derive through {@link deriveDueDate} so the rule can never drift.
 */
export const NET_TERMS_DAYS = 30;

/**
 * The due date for a given issue date: always issue + {@link NET_TERMS_DAYS}
 * days. Returns '' for a blank/unparseable issue date (nothing to derive).
 */
export function deriveDueDate(issueDate: string): string {
  return addDaysToDateString(issueDate, NET_TERMS_DAYS);
}

/**
 * The invoice/quote generator's initial form state + the per-type terms
 * wording, extracted from `generate-invoice-form.tsx`. No money is rendered
 * here (the discount/tax INPUTS are zeroed; sub-total/tax/total/balance are
 * derived by the `totals` memo, never stored), so it's wall-neutral; it also
 * lives under the financial-only-allowlisted `generate-invoice-form/` tree.
 */

/**
 * The terms line defaulted per document type — quotes carry a validity note,
 * invoices a due note. Shared by the initial state, the document-type switch,
 * and the preSelected-type sync effect so the wording lives in one place.
 */
export function defaultTerms(documentType: CrmDocumentType): string {
  return documentType === 'quote' ? 'Quote valid for 30 days' : 'Due upon receipt of Invoice';
}

/**
 * The blank form state: one default product line, today's issue date + a
 * 30-day due date, addresses empty (`U.S.A` country), and the discount/tax
 * inputs zeroed.
 */
export function createInitialFormData(documentType: CrmDocumentType) {
  return {
    customer_id: null as number | null,
    customer_deal_id: null as number | null,

    // Document Type
    document_type: documentType,

    // Invoice Details
    invoice_number: '',
    subject: '',
    issue_date: getLocalDateString(),
    due_date: deriveDueDate(getLocalDateString()),
    service_from: '',
    service_to: '',
    po_number: 'verbal',
    sales_person: '',
    terms: defaultTerms(documentType),

    // Addresses
    bill_to_company: '',
    bill_to_address: '',
    bill_to_city: '',
    bill_to_state: '',
    bill_to_zip: '',
    bill_to_country: 'U.S.A',

    ship_to_company: '',
    ship_to_address: '',
    ship_to_city: '',
    ship_to_state: '',
    ship_to_zip: '',
    ship_to_country: 'U.S.A',

    // Recipient contact person (one of the selected customer's own contacts).
    // `*_email` / `*_contact_name` mirror the picked contact for display; the
    // server snapshots them from the contact_id on save. Quotes use the bill_to
    // side as the single "To" recipient.
    bill_to_contact_id: null as number | null,
    bill_to_email: '',
    bill_to_contact_name: '',
    ship_to_contact_id: null as number | null,
    ship_to_email: '',
    ship_to_contact_name: '',

    // Products — one blank manual line.
    products: [createProductLine('1')],

    // Discount / tax INPUTS only. sub_total / tax_amount / total_amount /
    // balance_due are NOT stored here — they're derived from the line items +
    // these inputs by the `totals` memo (and re-derived server-side on save).
    market_discount: 0,
    reseller_discount: 0,
    additional_discount_amount: 0,
    additional_discount_percentage: 0,
    tax_percentage: 0,
  };
}

/** The form-state shape, derived from the factory so it can never drift. */
export type InvoiceFormData = ReturnType<typeof createInitialFormData>;

/**
 * Apply a single Document-Details field edit to the form state, keeping the
 * due date locked to issue + {@link NET_TERMS_DAYS}: whenever the operator
 * changes the issue date, the due date is re-derived from it. Every other
 * field is a plain assignment. Extracted (and pure) so the issue→due invariant
 * is unit-testable without mounting the whole form.
 *
 * The due date stays a normal editable field — the operator can still adjust
 * it afterward (e.g. to back-date a historical document); it just always
 * *starts* 30 days out from whatever issue date they pick.
 */
export function applyInvoiceFieldChange<K extends keyof InvoiceFormData>(
  prev: InvoiceFormData,
  field: K,
  value: InvoiceFormData[K],
): InvoiceFormData {
  const next = { ...prev, [field]: value } as InvoiceFormData;
  if (field === 'issue_date' && typeof value === 'string') {
    const derived = deriveDueDate(value);
    if (derived) next.due_date = derived;
  }
  return next;
}
